import { useState } from "react";
import {
  Box, Paper, TextField, InputAdornment, Alert, Button, Chip,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { DataGrid, type GridColDef, type GridPaginationModel, type GridRowParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customersApi, type CustomerDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { CustomerForm } from "./CustomerForm";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

export function CustomersPage() {
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerDto | null>(null);

  const canCreate = useAuth(s => s.hasPermission("sales.customer.create"));
  const canUpdate = useAuth(s => s.hasPermission("sales.customer.edit"));
  const canDelete = useAuth(s => s.hasPermission("sales.customer.delete"));
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["customers", search, pagination.page, pagination.pageSize],
    queryFn: () => customersApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customersApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (c: CustomerDto) => { setEditing(c); setFormOpen(true); };
  const handleDelete = (c: CustomerDto) => {
    if (confirm(`Delete customer "${c.name}"? Blocked if invoices exist.`)) {
      deleteMutation.mutate(c.id);
    }
  };

  const columns: GridColDef<CustomerDto>[] = [
    { field: "code", headerName: "Code", width: 120 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "uhid", headerName: "UHID", width: 140 },
    { field: "phone", headerName: "Phone", width: 130 },
    { field: "email", headerName: "Email", width: 200 },
    {
      field: "creditLimit", headerName: "Credit limit", width: 130, type: "number",
      valueFormatter: (v) => (v == null ? "—" : inr.format(Number(v))),
    },
    {
      field: "isActive", headerName: "Status", width: 100,
      renderCell: (p) => (
        <Chip size="small" label={p.value ? "Active" : "Inactive"} color={p.value ? "success" : "default"} />
      ),
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canUpdate, onClick: () => openEdit(p.row) },
          { label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canDelete, onClick: () => handleDelete(p.row) },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Catalog"]} title="Customers"
        actions={<>{canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Customer
          </Button>
        )}</>}
      />

      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code, name, phone or UHID"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p: GridRowParams<CustomerDto>) => canUpdate && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <CustomerForm open={formOpen} onClose={() => setFormOpen(false)} customer={editing} />
    </Box>
  );
}
